import dayjs from 'dayjs'
import {useCallback, useMemo} from 'react'
import {useController, useFormContext} from 'react-hook-form'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trash01Icon} from '@/components/ui/icon/Icon'
import {InputSelectPathology} from '@/features/projects/components/input-select-pathology/InputSelectPathology'
import {getDiagnosisSelectionVersion, getFirstMappedCode} from '@/features/projects/diagnosisVersion'
import {MainPathologiesFormSchema} from '@/features/projects/formSchemas'
import {Diagnosis} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'

interface ProjectMainPathologiesFieldsProps {
    index: number
    disabledIcd9: string[]
    disabledIcd10: string[]
    showTrashButton: boolean
    remove: (index: number) => void
}

export const ProjectMainPathologiesFields = ({
    index,
    disabledIcd9,
    disabledIcd10,
    showTrashButton,
    remove
}: ProjectMainPathologiesFieldsProps) => {
    const {t} = useTranslation()
    const {control} = useFormContext<MainPathologiesFormSchema>()
    const {field} = useController({control, name: `mainPathologies.${index}`})

    // From 2027 ICD-10 is the only classification that may be picked: ICD-9 stays visible, but
    // filled in from the ICD-10 rather than searched.
    const isIcd9Selectable = getDiagnosisSelectionVersion() == 'ICD9'

    const selectedStartDate = useMemo(() => {
        return field.value.startedAt ? dayjs(field.value.startedAt).toDate() : null
    }, [field.value])

    // The two codes are separate fields. Picking an ICD-9 only proposes an ICD-10 while that one is
    // still empty: the transcoding helps the first fill-in, it does not tie the two together.
    const handleSelectIcd9 = useCallback(
        (value: Diagnosis | null) => {
            field.onChange(
                {
                    ...field.value,
                    diagnosis: value,
                    icd10Diagnosis: field.value.icd10Diagnosis ?? getFirstMappedCode(value)
                },
                {shouldDirty: true}
            )
        },
        [field.onChange, field.value]
    )

    // Never touches the ICD-9: the doctor picks the ICD-10 from the whole list, not from the codes
    // mapped to the ICD-9.
    const handleSelectIcd10 = useCallback(
        (value: Diagnosis | null) => {
            field.onChange({...field.value, icd10Diagnosis: value}, {shouldDirty: true})
        },
        [field.onChange, field.value]
    )

    const handleSelectStartDate = useCallback(
        (value: Date | null) => {
            field.onChange(
                {...field.value, startedAt: value ? dayjs(value).format('YYYY-MM-DD') : null},
                {
                    shouldDirty: true
                }
            )
        },
        [field.onChange, field.value]
    )

    return (
        <Flexbox fullWidth justify="space-between" gap={2.5}>
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd9_cm', {number: index + 1})}
                    placeholder={t('projects:pathology_number_placeholder')}
                    value={field.value.diagnosis}
                    onChange={handleSelectIcd9}
                    disabledOptions={disabledIcd9}
                    disabled={!isIcd9Selectable}
                    version="ICD9"
                />
            </div>
            <div style={{flex: '1 1 0', minWidth: 0}}>
                <InputSelectPathology
                    label={t('projects:pathology_number_icd10_im', {number: index + 1})}
                    placeholder={t('projects:pathology_number_icd10_im_placeholder')}
                    value={field.value.icd10Diagnosis}
                    onChange={handleSelectIcd10}
                    disabledOptions={disabledIcd10}
                    version="ICD10"
                />
            </div>
            <Flexbox gap={1} align="flex-end" style={{flex: '0 0 auto'}}>
                <InputDate
                    label={t('projects:start_date')}
                    placeholder={'GG/MM/AAAA'}
                    value={selectedStartDate}
                    onChange={handleSelectStartDate}
                    onMonthChange={handleSelectStartDate}
                    disableAfterDate={new Date()}
                />
                {showTrashButton && (
                    <Button variant="ghost" shape="square" onClick={() => remove(index)}>
                        <Trash01Icon />
                    </Button>
                )}
            </Flexbox>
        </Flexbox>
    )
}
